import { format, isValid, parse } from "date-fns";

export type UpcomingSessionStatus = "PAST" | "TODAY" | "UPCOMING";

// A null severity renders as a muted (gray) tag
type SessionStatusDisplay = { label: string; severity: "success" | "info" | null };

const STATUS_DISPLAY: Record<UpcomingSessionStatus, SessionStatusDisplay> = {
  PAST: { label: "Past", severity: null },
  TODAY: { label: "Today", severity: "success" },
  UPCOMING: { label: "Upcoming", severity: "info" },
};

export const getSessionStatusDisplay = (
  status: string
): SessionStatusDisplay =>
  STATUS_DISPLAY[status as UpcomingSessionStatus] ?? {
    label: status || "-",
    severity: null,
  };

// The API sends the session's calendar date (YYYY-MM-DD); parse it as a local
// date so no time zone shift can move it to another day.
export const formatSessionDate = (date: string) => {
  const parsed = parse(date, "yyyy-MM-dd", new Date());
  return isValid(parsed) ? format(parsed, "MMM d, yyyy") : date;
};

export const formatSessionTime = (time?: string | null) => {
  if (!time) return "";
  const parsed = parse(time, "HH:mm", new Date());
  return isValid(parsed) ? format(parsed, "h:mm a") : time;
};

// Query-string keys used to carry the dashboard filters to the full page
export const SESSION_FILTER_PARAMS = {
  dateFilter: "date_filter",
  startDate: "start_date",
  endDate: "end_date",
  category: "category",
} as const;

export interface SessionFilterValues {
  dateFilter?: string;
  startDate?: string;
  endDate?: string;
  category?: string;
}

export const buildSessionFilterQuery = (values: SessionFilterValues) => {
  const query = new URLSearchParams();
  (Object.keys(SESSION_FILTER_PARAMS) as Array<keyof SessionFilterValues>)
    .filter((key) => !!values[key])
    .forEach((key) =>
      query.set(SESSION_FILTER_PARAMS[key], values[key] as string),
    );
  const text = query.toString();
  return text ? `?${text}` : "";
};
